import React from "react";
import { useDispatch } from "react-redux";
import { useSnackbar } from "notistack";
import { Box, Button, CircularProgress, IconButton, Paper, Stack, Typography } from "@mui/material";
import { Icon } from "@iconify/react";
import { CopyToClipboard } from "react-copy-to-clipboard";
import { useSocket } from "../../../socket/SocketContext";
import { joinRandomSession, cancelRandomSession } from "../../../redux/features/lmsSlice";

function sessionStorageGet(key) {
    try { return sessionStorage.getItem(key); } catch { return null; }
}
function sessionStorageSet(key, value) {
    try { sessionStorage.setItem(key, value); } catch { /* private-browsing / storage disabled — fine, just no persistence */ }
}
function sessionStorageRemove(key) {
    try { sessionStorage.removeItem(key); } catch { /* */ }
}

/** Random 1:1 — employee-portal integration step 2. Click "Find a partner": either matched immediately
 *  (the request's own response) or queued, in which case the match arrives later over the socket
 *  ('lms_random_matched' on the existing /chat namespace — see node-portal's lmsRandomQueue.js). Both
 *  matched people join the SAME LMS room as equal peers, no host/student distinction. */
// Per-tab persisted match, same pattern the LMS's own web app uses for its join state (Room.tsx's
// sessionStorage name/pid) — the room genuinely exists once matched, so a refresh must not lose access
// to it. NOT used for "waiting": that's not a durable fact (their old socket is gone after a refresh;
// the backend's own connectivity check already cleans up a stale queue entry), so re-showing a fake
// "waiting" spinner post-refresh would be misleading — idle + a real re-click is the honest behavior.
const MATCH_STORAGE_KEY = "lms_random_last_match";

export default function RandomSessionPage() {
    const dispatch = useDispatch();
    const socket = useSocket();
    const { enqueueSnackbar } = useSnackbar();
    const [state, setState] = React.useState(() => (sessionStorageGet(MATCH_STORAGE_KEY) ? "matched" : "idle"));
    const [link, setLink] = React.useState(() => sessionStorageGet(MATCH_STORAGE_KEY));
    const [error, setError] = React.useState(null);
    // Read via ref in the unmount cleanup below — a plain closure over `state` would see whatever it was
    // when the effect FIRST ran (mount), not the latest value, since that effect's deps are intentionally
    // empty (it must only fire once, on unmount).
    const stateRef = React.useRef(state);
    stateRef.current = state;

    const persistMatch = (l) => {
        sessionStorageSet(MATCH_STORAGE_KEY, l);
        setLink(l);
        setState("matched");
    };

    React.useEffect(() => {
        if (!socket) return;
        const onMatched = (data) => persistMatch(data.link);
        socket.on("lms_random_matched", onMatched);
        return () => socket.off("lms_random_matched", onMatched);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [socket]);

    // Navigating to a different portal page while still "waiting" would otherwise strand the queue entry
    // forever: the /chat socket that the backend uses to check "are they still around" stays connected
    // app-wide (it's not specific to this page), so the backend's own stale-waiter cleanup never catches
    // this case — only an actual disconnect (tab close) does. Cancel explicitly on unmount to cover it.
    React.useEffect(() => {
        return () => {
            if (stateRef.current === "waiting") dispatch(cancelRandomSession());
        };
    }, [dispatch]);

    const handleFind = () => {
        sessionStorageRemove(MATCH_STORAGE_KEY); // starting a fresh search — forget any earlier match
        setError(null);
        setState("waiting");
        dispatch(joinRandomSession()).then((res) => {
            const status = res.payload?.status;
            if (status === "matched") {
                persistMatch(res.payload.link);
            } else if (status === "waiting") {
                // Stay in the waiting state — the match (if any) arrives later via the socket.
            } else {
                setState("error");
                setError(res.payload?.message || "Could not start matchmaking");
            }
        });
    };

    const handleCancel = () => {
        dispatch(cancelRandomSession());
        setState("idle");
    };

    return (
        <Box sx={{ maxWidth: 560, mx: "auto", mt: 4 }}>
            <Paper sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>Random 1:1</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Get matched with the next person who also clicks — you'll both land in the same class.
                </Typography>

                {state === "idle" && (
                    <Button variant="contained" onClick={handleFind}>Find a partner</Button>
                )}

                {state === "waiting" && (
                    <Stack direction="row" alignItems="center" spacing={2}>
                        <CircularProgress size={20} />
                        <Typography variant="body2">Waiting for someone else to join…</Typography>
                        <Button size="small" onClick={handleCancel}>Cancel</Button>
                    </Stack>
                )}

                {state === "matched" && link && (
                    <>
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                            <Typography variant="body2" sx={{ wordBreak: "break-all", flexGrow: 1 }}>{link}</Typography>
                            <CopyToClipboard text={link} onCopy={() => enqueueSnackbar("Copied", { variant: "success" })}>
                                <IconButton size="small">
                                    <Icon icon="bx:copy-alt" width="20" height="20" color="#2f72b3" />
                                </IconButton>
                            </CopyToClipboard>
                            <Button size="small" variant="contained" href={link} target="_blank" rel="noopener noreferrer">
                                Join
                            </Button>
                        </Stack>
                        {/* This link is remembered across a refresh (sessionStorage) — an explicit action is
                            needed to move on, otherwise it would sit here forever with no way out. */}
                        <Button size="small" onClick={handleFind}>Find another partner</Button>
                    </>
                )}

                {state === "error" && (
                    <>
                        <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>
                        <Button variant="contained" onClick={handleFind}>Try again</Button>
                    </>
                )}
            </Paper>
        </Box>
    );
}
